import type { ZudokuConfig } from "zudoku";

const config: ZudokuConfig = {
  site: {
    title: "Documentation | Digitoo.ai",
    logo: {
      src: {
        dark: "https://digitoominioprodv2.blob.core.windows.net/public/logo/dark.svg",
        light:
          "https://digitoominioprodv2.blob.core.windows.net/public/logo/light.svg",
      },
      width: "160px",
      alt: "Digitoo.ai",
    },
    footer: {
      columns: [
        {
          title: "Product",
          links: [{ label: "Features", href: "https://digitoo.ai" }],
        },
        {
          title: "Company",
          links: [
            { label: "About", href: "https://digitoo.ai/about-us" },
            { label: "Blog", href: "https://digitoo.ai/blog" },
            { label: "Careers", href: "https://digitoo.ai/career" },
          ],
        },
        {
          title: "Resources",
          links: [
            { label: "API Reference", href: "/documentation/api-v2-reference" },
            { label: "Status", href: "https://status.digitoo.ai" },
            { label: "Support", href: "https://digitoo.ai/contact" },
          ],
        },
        {
          title: "Legal",
          links: [
            { label: "Privacy", href: "https://www.digitoo.ai/privacy-policy" },
            { label: "Terms", href: "https://www.digitoo.ai/conditions" },
          ],
        },
      ],
      copyright: `© ${new Date().getFullYear()} Digitoo.ai. All rights reserved.`,
      logo: {
        src: {
          dark: "https://digitoominioprodv2.blob.core.windows.net/public/logo/dark.svg",
          light:
            "https://digitoominioprodv2.blob.core.windows.net/public/logo/light.svg",
        },
        alt: "Digitoo.ai",
        width: "120px",
      },
    },
  },

  metadata: {
    title: "Documentation | Digitoo.ai",
    favicon: "/public/favicon.ico",
  },

  search: {
    type: "pagefind",
  },

  navigation: [
    {
      type: "category",
      label: "Documentation",
      collapsed: false,
      items: [
        "documentation",
        "authorization",
        "openapi",
        {
          type: "category",
          label: "Processes",
          collapsed: false,
          items: [
            "documentation/processes/extracting-data",
            "documentation/processes/supported-file-types",
            "documentation/processes/email-import",
            "documentation/processes/erp-integration",
            "documentation/processes/embedded-access",
            "documentation/processes/webhooks",
          ],
        },
        {
          type: "category",
          label: "Entities",
          collapsed: false,
          items: [
            "documentation/entities/hierarchy",
            {
              type: "category",
              label: "Document",
              collapsed: true,
              items: [
                "documentation/entities/document",
                "documentation/entities/document/include-fields",
                "documentation/entities/document/filters",
                "documentation/entities/document/tax-detail",
                "documentation/entities/document/line-items",
              ],
            },
            {
              type: "category",
              label: "Queue",
              collapsed: true,
              items: [
                "documentation/entities/queue",
                "documentation/entities/queue/data-types",
                "documentation/entities/queue/annotation-schema",
                "documentation/entities/queue/example-annotation-schemas",
              ],
            },
            "documentation/entities/register",
          ],
        },
      ],
    },
    {
      type: "link",
      to: "/api-reference",
      label: "API Reference",
    },
  ],

  redirects: [
    { from: "/", to: "/documentation" },
    { from: "/documentation/api-v2-reference", to: "/api-reference" },
    {
      from: "/documentation/api-v2-reference/registers",
      to: "/api-reference/registers",
    },
  ],

  apis: {
    type: "file",
    input: "./apis/openapi.json",
    path: "/api-reference",
    options: {
      expandAllTags: false,
    },
  },

  defaults: {
    apis: {
      transformExamples: ({ content }) => {
        return content;
      },
    },
  },

  docs: {
    files: "/pages/**/*.{md,mdx}",
  },

  theme: {
    light: {
      background: "0 0% 100%",
      foreground: "222.2 84% 4.9%",
      card: "0 0% 100%",
      cardForeground: "222.2 84% 4.9%",
      popover: "0 0% 100%",
      popoverForeground: "222.2 84% 4.9%",
      primary: "221.2 83.2% 53.3%",
      primaryForeground: "210 40% 98%",
      secondary: "210 40% 96.1%",
      secondaryForeground: "222.2 47.4% 11.2%",
      muted: "210 40% 96.1%",
      mutedForeground: "215.4 16.3% 46.9%",
      accent: "210 40% 96.1%",
      accentForeground: "222.2 47.4% 11.2%",
      destructive: "0 84.2% 60.2%",
      destructiveForeground: "210 40% 98%",
      border: "214.3 31.8% 91.4%",
      input: "214.3 31.8% 91.4%",
      ring: "221.2 83.2% 53.3%",
      radius: "0.6rem",
    },
    dark: {
      background: "222.2 84% 4.9%",
      foreground: "210 40% 98%",
      card: "222.2 84% 4.9%",
      cardForeground: "210 40% 98%",
      popover: "222.2 84% 4.9%",
      popoverForeground: "210 40% 98%",
      primary: "217.2 91.2% 59.8%",
      primaryForeground: "222.2 47.4% 11.2%",
      secondary: "217.2 32.6% 17.5%",
      secondaryForeground: "210 40% 98%",
      muted: "217.2 32.6% 17.5%",
      mutedForeground: "215 20.2% 65.1%",
      accent: "217.2 32.6% 17.5%",
      accentForeground: "210 40% 98%",
      destructive: "0 62.8% 30.6%",
      destructiveForeground: "210 40% 98%",
      border: "217.2 32.6% 17.5%",
      input: "217.2 32.6% 17.5%",
      ring: "224.3 76.3% 48%",
      radius: "0.6rem",
    },
  },
};

export default config;
